import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, RefreshCw, Loader2, AlertTriangle, Plus, Minus,
  Printer, CheckCircle2, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STATUS_BADGE = {
  excess: { label: 'Excess', className: 'bg-amber-100 text-amber-800 border-amber-300' },
  shortage: { label: 'Shortage', className: 'bg-rose-100 text-rose-800 border-rose-300' },
};

function SummaryTile({ label, value, accent = 'stone' }) {
  return (
    <Card className={`p-4 border-${accent}-200`}>
      <div className="text-xs uppercase tracking-wide text-stone-500">{label}</div>
      <div className={`text-3xl font-semibold mt-1 text-${accent}-700`}>{value}</div>
    </Card>
  );
}

export default function InhouseBundleReconciliation() {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [summary, setSummary] = useState({ total_bundles: 0, excess_bundles: 0, shortage_bundles: 0, total_usns_to_add: 0, total_usns_to_remove: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Reconcile dialog state
  const [activeBundle, setActiveBundle] = useState(null); // detail row
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [selectedToRemove, setSelectedToRemove] = useState([]); // shortage flow
  const [resultUsns, setResultUsns] = useState(null); // { added: [...], type: 'add'|'remove' }

  const fetchBundles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/processing-inward/admin/legacy-qty-mismatched-bundles', {
        params: { status_filter: statusFilter, search: search.trim(), limit: 1000 },
      });
      setBundles(data.bundles || []);
      setSummary({
        total_bundles: data.total_bundles || 0,
        excess_bundles: data.excess_bundles || 0,
        shortage_bundles: data.shortage_bundles || 0,
        total_usns_to_add: data.total_usns_to_add || 0,
        total_usns_to_remove: data.total_usns_to_remove || 0,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load mismatched bundles');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchBundles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openReconcile = async (bundleCode) => {
    setReason('');
    setSelectedToRemove([]);
    setResultUsns(null);
    try {
      // Bundle codes contain '/', the backend uses {bundle_code:path} which
      // accepts slashes literally. Send raw (do NOT encodeURIComponent the slash).
      const { data } = await apiClient.get(`/processing-inward/admin/legacy-bundle-detail/${bundleCode}`);
      setActiveBundle(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bundle detail');
    }
  };

  const closeDialog = () => {
    setActiveBundle(null);
    setReason('');
    setSelectedToRemove([]);
    setResultUsns(null);
  };

  const submitAdd = async () => {
    if (!activeBundle) return;
    setSubmitting(true);
    try {
      const { data } = await apiClient.post('/processing-inward/admin/legacy-add-bundle-usns', {
        bundle_code: activeBundle.bundle_code,
        count: 0, // auto-pick to match received_qty
        reason: reason.trim(),
      });
      setResultUsns({ type: 'add', usns: data.added_usns || [] });
      toast.success(`Added ${data.added_usns?.length || 0} USN(s) to ${activeBundle.bundle_code}`);
      fetchBundles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add USNs');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRemove = async () => {
    if (!activeBundle) return;
    if (selectedToRemove.length === 0) {
      toast.error('Select at least one USN to remove');
      return;
    }
    const expected = activeBundle.current_usn_count - activeBundle.received_qty;
    if (selectedToRemove.length !== expected) {
      toast.error(`Select exactly ${expected} USN(s) — currently ${selectedToRemove.length} selected`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post('/processing-inward/admin/legacy-remove-bundle-usns', {
        bundle_code: activeBundle.bundle_code,
        usns_to_remove: selectedToRemove,
        reason: reason.trim(),
      });
      setResultUsns({ type: 'remove', usns: data.removed_usns || [] });
      toast.success(`Removed ${data.removed_usns?.length || 0} USN(s) from ${activeBundle.bundle_code}`);
      fetchBundles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove USNs');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUsnSelect = (usn) => {
    setSelectedToRemove((prev) => (
      prev.includes(usn) ? prev.filter((x) => x !== usn) : [...prev, usn]
    ));
  };

  const goPrintUsn = (usn) => {
    navigate('/wms/fg/usn-label-reprint', { state: { presetUsn: usn } });
  };

  const dlg = activeBundle;
  const isExcess = dlg && dlg.delta > 0;
  const isShortage = dlg && dlg.delta < 0;
  const expectedRemoveCount = dlg ? dlg.current_usn_count - dlg.received_qty : 0;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-base font-semibold">Inhouse Bundle Reconciliation</h1>
            <p className="text-xs text-stone-500">Fix legacy bundles where Bundle Receipt Qty ≠ Processing Inward Qty</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchBundles} disabled={loading} data-testid="refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryTile label="Total Bundles" value={summary.total_bundles} accent="stone" />
          <SummaryTile label="Excess Bundles" value={summary.excess_bundles} accent="amber" />
          <SummaryTile label="Shortage Bundles" value={summary.shortage_bundles} accent="rose" />
          <SummaryTile label="USNs to Add" value={summary.total_usns_to_add} accent="emerald" />
          <SummaryTile label="USNs to Remove" value={summary.total_usns_to_remove} accent="rose" />
        </div>

        {/* Filters */}
        <Card className="p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="flex-1 flex items-center gap-2">
            <Search className="h-4 w-4 text-stone-400" />
            <Input
              placeholder="Search bundle code, design, or sort no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchBundles()}
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="sm:w-48" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="excess">Excess only</SelectItem>
              <SelectItem value="shortage">Shortage only</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchBundles} disabled={loading} data-testid="search-btn">
            <Search className="h-4 w-4 mr-1" />
            Search
          </Button>
        </Card>

        {/* Table */}
        <Card>
          <Table data-testid="reconciliation-table">
            <TableHeader>
              <TableRow>
                <TableHead>Bundle Code</TableHead>
                <TableHead>Design / Shade</TableHead>
                <TableHead>Sort No / Vendor</TableHead>
                <TableHead className="text-right">BRE Qty</TableHead>
                <TableHead className="text-right">PI Qty</TableHead>
                <TableHead className="text-right">Current USNs</TableHead>
                <TableHead className="text-right">Delta</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={9} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading...</TableCell></TableRow>
              )}
              {!loading && bundles.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-12 text-stone-500">
                  <CheckCircle2 className="h-8 w-8 inline-block text-emerald-500 mb-2" /><br />
                  No legacy mismatches — all bundles in sync.
                </TableCell></TableRow>
              )}
              {!loading && bundles.map((b) => {
                const isEx = (b.delta || 0) > 0;
                const isSh = (b.delta || 0) < 0;
                const badge = isEx ? STATUS_BADGE.excess : (isSh ? STATUS_BADGE.shortage : null);
                return (
                  <TableRow key={b.bundle_code} data-testid={`row-${b.bundle_code}`}>
                    <TableCell className="font-mono text-sm">{b.bundle_code}</TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{b.design_name || '-'}</div>
                      <div className="text-xs text-stone-500">{b.shade || '-'}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{b.sort_no || '-'}</div>
                      <div className="text-xs text-stone-500 truncate max-w-[180px]">{b.vendor_name || '-'}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{b.sent_qty}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{b.received_qty}</TableCell>
                    <TableCell className="text-right tabular-nums">{b.current_usn_count}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${isEx ? 'text-amber-700' : 'text-rose-700'}`}>
                      {b.delta > 0 ? `+${b.delta}` : b.delta}
                    </TableCell>
                    <TableCell>
                      {badge && <Badge className={badge.className} variant="outline">{badge.label}</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant={isEx ? 'default' : 'destructive'}
                        onClick={() => openReconcile(b.bundle_code)}
                        data-testid={`reconcile-${b.bundle_code}`}
                      >
                        {isEx ? <><Plus className="h-3 w-3 mr-1" />Add USNs</> : <><Minus className="h-3 w-3 mr-1" />Remove USNs</>}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </main>

      {/* Reconcile Dialog */}
      <Dialog open={!!activeBundle} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-2xl" data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isExcess ? <Plus className="h-5 w-5 text-amber-600" /> : <Minus className="h-5 w-5 text-rose-600" />}
              {isExcess ? 'Add USNs (Excess)' : 'Remove USNs (Shortage)'}
              {dlg && <span className="font-mono text-base text-stone-600 ml-2">{dlg.bundle_code}</span>}
            </DialogTitle>
            <DialogDescription>
              {isExcess && `Bundle has ${Math.abs(dlg.delta)} fewer USN(s) than the Processing Inward qty. New USNs will be claimed from the PI pool.`}
              {isShortage && `Bundle has ${Math.abs(dlg.delta)} more USN(s) than the Processing Inward qty. Select exactly ${expectedRemoveCount} USN(s) to release back to the pool.`}
            </DialogDescription>
          </DialogHeader>

          {dlg && !resultUsns && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-stone-500">Design</div><div className="font-medium">{dlg.design_name || '-'}</div></div>
                <div><div className="text-xs text-stone-500">Shade</div><div className="font-medium">{dlg.shade || '-'}</div></div>
                <div><div className="text-xs text-stone-500">BRE Qty</div><div className="font-medium">{dlg.sent_qty}</div></div>
                <div><div className="text-xs text-stone-500">PI Qty (target)</div><div className="font-semibold text-emerald-700">{dlg.received_qty}</div></div>
                <div><div className="text-xs text-stone-500">Current USNs</div><div className="font-medium">{dlg.current_usn_count}</div></div>
                <div><div className="text-xs text-stone-500">Bundle Status</div><div className="font-medium">{dlg.bundle_status || '-'}</div></div>
              </div>

              {isShortage && (
                <div className="border rounded-md">
                  <div className="px-3 py-2 bg-stone-50 border-b text-sm font-medium flex items-center justify-between">
                    <span>Select {expectedRemoveCount} USN(s) to remove</span>
                    <Badge variant="outline" className={selectedToRemove.length === expectedRemoveCount ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-amber-50 text-amber-800 border-amber-300'}>
                      {selectedToRemove.length} / {expectedRemoveCount} selected
                    </Badge>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {(dlg.current_usns || []).map((usn) => (
                      <label
                        key={usn}
                        className="flex items-center gap-3 px-3 py-2 border-b hover:bg-stone-50 cursor-pointer"
                        data-testid={`usn-row-${usn}`}
                      >
                        <Checkbox
                          checked={selectedToRemove.includes(usn)}
                          onCheckedChange={() => toggleUsnSelect(usn)}
                          data-testid={`usn-checkbox-${usn}`}
                        />
                        <span className="font-mono text-sm flex-1">{usn}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="e.g., legacy bundle reconciliation per physical recount"
                  data-testid="reason-textarea"
                />
              </div>

              {isExcess && (
                <Alert className="bg-amber-50 border-amber-300">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-800">PI USN Pool</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    {Math.abs(dlg.delta)} fresh USN(s) will be claimed from the PI pool and appended to this bundle.
                    Print stickers via FG Label Reprint after success.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Result panel */}
          {resultUsns && (
            <div className="space-y-3">
              <Alert className="bg-emerald-50 border-emerald-300">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-800">
                  {resultUsns.type === 'add' ? 'USNs Added' : 'USNs Removed'}
                </AlertTitle>
                <AlertDescription className="text-emerald-700">
                  {resultUsns.type === 'add'
                    ? `${resultUsns.usns.length} fresh USN(s) added to ${dlg.bundle_code}. Print stickers below.`
                    : `${resultUsns.usns.length} USN(s) released back to the PI pool from ${dlg.bundle_code}.`}
                </AlertDescription>
              </Alert>
              <div className="border rounded-md max-h-64 overflow-y-auto">
                {resultUsns.usns.map((u) => (
                  <div key={u} className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="font-mono text-sm">{u}</span>
                    {resultUsns.type === 'add' && (
                      <Button size="sm" variant="outline" onClick={() => goPrintUsn(u)} data-testid={`print-${u}`}>
                        <Printer className="h-3 w-3 mr-1" />
                        Print
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            {!resultUsns && (
              <>
                <Button variant="ghost" onClick={closeDialog} disabled={submitting} data-testid="cancel-btn">Cancel</Button>
                {isExcess && (
                  <Button onClick={submitAdd} disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-add-btn">
                    {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Add {Math.abs(dlg?.delta || 0)} USN(s)
                  </Button>
                )}
                {isShortage && (
                  <Button
                    onClick={submitRemove}
                    disabled={submitting || selectedToRemove.length !== expectedRemoveCount}
                    variant="destructive"
                    data-testid="confirm-remove-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                    Remove {selectedToRemove.length} USN(s)
                  </Button>
                )}
              </>
            )}
            {resultUsns && (
              <Button onClick={closeDialog} data-testid="close-btn">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
